import React, { useState } from 'react';
import {
  Bell, X, ChevronDown, ChevronUp,
  Calendar, User, FileText, Hash, Mail, CheckCircle
} from 'lucide-react';

const COMPONENT_META = {
  fms:        { label: 'FMS',        icon: FileText,   color: 'var(--c-purple)',  bg: 'var(--c-purple-bg)' },
  delegation: { label: 'Delegation', icon: User,       color: 'var(--teal-primary)', bg: 'var(--teal-dim)' },
  ht:         { label: 'HT Tasks',   icon: Mail,       color: 'var(--c-danger)',  bg: 'var(--c-danger-bg)' },
  pc:         { label: 'PC Tasks',   icon: CheckCircle,color: 'var(--c-success)', bg: 'var(--c-success-bg)' },
};
const getMeta = (c) => COMPONENT_META[c] || { label: c.toUpperCase(), icon: Bell, color: 'var(--gold-primary)', bg: 'var(--gold-dim)' };

const formatTaskPreview = (task, component) => {
  switch (component) {
    case 'fms':
    case 'pc': {
      const info = task.task_info || '';
      const orderId  = (info.match(/(?:Oder ID|Order ID) - ([^<\n]+)/) || [])[1]?.trim();
      const client   = (info.match(/Name of the Client - ([^<\n]+)/) || [])[1]?.trim();
      const amount   = (info.match(/Invoice Amount - ([^<\n]+)/) || [])[1]?.trim();
      return { title: orderId || `Task ${task.id}`, subtitle: client || task.doer || '—', detail: amount ? `₹${amount}` : task.fms || '', date: task.planned || task.week || '' };
    }
    case 'delegation':
      return { title: task.task || `Delegation Task ${task.id}`, subtitle: task.company || '—', detail: task.doer_name || task.assignee || '', date: task.task_created_date || task.first_date || '' };
    case 'ht':
      return { title: task.ticketId || `HT-${task.id}`, subtitle: task.challengeIssue || task.name || '—', detail: task.issueDelegatedTo || task.department || '', date: task.timestamp || task.replyPlanned || '' };
    default:
      return { title: `New Task ${task.id}`, subtitle: '', detail: '', date: '' };
  }
};

const NewTaskNotification = ({ notifications, onClose, onMarkAllRead }) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  if (!notifications || notifications.length === 0) return null;

  const total = notifications.reduce((s, n) => s + n.count, 0);
  const toggle = (c) => setExpandedGroups(p => ({ ...p, [c]: !p[c] }));

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80, zIndex: 9000, padding: '80px 16px 16px' }}
    >
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 20,
        width: '100%', maxWidth: 560,
        maxHeight: '80vh',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-xl)',
        display: 'flex', flexDirection: 'column',
      }}>

        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg, var(--green-primary) 0%, #133d25 100%)',
          padding: '18px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.20)',
            }}>
              <Bell style={{ width: 18, height: 18, color: '#fff' }} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px', margin: 0 }}>
                New Tasks Available!
              </h3>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.60)', margin: 0, marginTop: 2 }}>
                {total} new task{total !== 1 ? 's' : ''} across {notifications.length} module{notifications.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#fff', display: 'flex', lineHeight: 0 }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* ── Notification Groups ── */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {notifications.map((notif, i) => {
            const meta = getMeta(notif.component);
            const Icon = meta.icon;
            const expanded = expandedGroups[notif.component];

            return (
              <div key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                {/* Group row */}
                <div
                  onClick={() => toggle(notif.component)}
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon style={{ width: 17, height: 17, color: meta.color }} />
                    </div>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.2px' }}>
                        {meta.label}
                      </p>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, marginTop: 1 }}>
                        {notif.count} new task{notif.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
                      background: 'var(--gold-dim)', color: 'var(--gold-primary)',
                      border: '1px solid rgba(200,146,42,0.25)',
                    }}>
                      +{notif.count}
                    </span>
                    {expanded
                      ? <ChevronUp  style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
                      : <ChevronDown style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
                    }
                  </div>
                </div>

                {/* Expanded task list */}
                {expanded && (
                  <div style={{ background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border)', maxHeight: 200, overflowY: 'auto' }}>
                    {notif.newTasks.slice(0, 10).map((task, ti) => {
                      const p = formatTaskPreview(task, notif.component);
                      return (
                        <div key={ti} style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.1px' }}>
                                {p.title}
                              </p>
                              {p.subtitle && (
                                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>{p.subtitle}</p>
                              )}
                              {(p.detail || p.date) && (
                                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                                  {p.detail && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                                      <Hash style={{ width: 10, height: 10 }} />{p.detail}
                                    </span>
                                  )}
                                  {p.date && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                                      <Calendar style={{ width: 10, height: 10 }} />{p.date}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green-primary)', flexShrink: 0, marginTop: 5 }} />
                          </div>
                        </div>
                      );
                    })}
                    {notif.newTasks.length > 10 && (
                      <div style={{ padding: '10px 20px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                        +{notif.newTasks.length - 10} more tasks
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-surface-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
            Last updated: {new Date().toLocaleTimeString()}
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={onMarkAllRead}
              style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 10px', borderRadius: 8 }}
            >
              Mark as Read
            </button>
            <button
              onClick={onClose}
              style={{
                fontSize: 13, fontWeight: 700,
                padding: '7px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, var(--gold-primary), var(--gold-light))',
                color: '#1a1a1a',
                boxShadow: '0 2px 10px rgba(200,146,42,0.30)',
                letterSpacing: '-0.1px',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTaskNotification;
